import { ChangeDetectionStrategy, Component, Optional, ViewEncapsulation, booleanAttribute, computed, inject, input } from '@angular/core';
import { renderMarkdownCached }                                                  from './cs-markdown.renderer';
import { CS_CHAT_GLOSSARY, CsGlossarySource, decorateGlossaryHtml }              from './cs-glossary';

/**
 * Renders an assistant message body as markdown. The parsed HTML is bound through
 * `[innerHTML]`, so Angular's sanitizer runs over it on every change.
 *
 * The heavy lifting (parsing) is memoised by {@link renderMarkdownCached}: a
 * finished message is parsed once and served from cache forever after, while a
 * still-streaming message bypasses the cache and re-renders on each delta. Being
 * OnPush with signal inputs, this component only recomputes when `text` or
 * `streaming` actually changes — so unchanged messages cost nothing on scroll.
 *
 * Encapsulation is None on purpose: `[innerHTML]` content never receives Angular's
 * emulated-encapsulation attributes, so scoped styles would not reach the parsed
 * elements. All selectors are nested under `.cs-markdown`, so they stay effectively
 * scoped to rendered markdown despite being global.
 */
@Component({
	standalone:      false,
	selector:        'cs-markdown',
	template:        '<div class="cs-markdown" [innerHTML]="html()"></div>',
	styleUrls:       ['./cs-markdown.component.scss'],
	encapsulation:   ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsMarkdownComponent {
	readonly text = input.required<string>();
	readonly streaming = input(false, { transform: booleanAttribute });

	private readonly glossary = inject(CS_CHAT_GLOSSARY, { optional: true }) as CsGlossarySource | null;

	protected readonly html = computed(() => {
		const raw = renderMarkdownCached(this.text(), this.streaming());
		// Don't decorate a half-streamed message — partial words would mis-match and reflow.
		if (this.streaming()) { return raw; }
		const terms = this.glossary?.terms() ?? [];
		return terms.length ? decorateGlossaryHtml(raw, terms) : raw;
	});
}
